/**
 * The daily-log form (PF-2, fieldiq-api#1200), shared by the office page and
 * the phone. The two surfaces read different casings (office camelCase, the
 * /field surface snake_case); each screen maps its read into the plain shapes
 * below, so the form, its rules and the body it sends are one.
 *
 * ONE ROW PER CREW MEMBER AND PER UNIT, hours blank = not on the log. The
 * body always sends `crew` and `equipment`, and the API REPLACES each set, so
 * a row cleared here is a row removed there.
 *
 * - Hours and standby are 0–24. Chainage is in the tenant's units; both ends
 *   or neither, and to ≥ from.
 * - The hour-meter end is offered on an OWNED unit only (the API refuses it on
 *   a rent-in: meter_owned_only).
 * - A rent-in is offered only on a day it is on rent (the API refuses the
 *   rest: rent_in_not_on_rent), unless the log already holds it.
 * - The pre-use check picker lists that day's checks on that unit, from
 *   GET /jobs/:id/pre-use-checks?date= — a unit with no registered equipment
 *   (an unregistered rent-in) has no checks to pick.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck } from 'lucide-react';
import { blankToNull, pfDay, toInput } from '../../lib/pipeline-facility';
import type { PfDailyLogWrite, PfPreUseCheck } from '../../types/pipeline-facility';
import { PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfField, PfFormError } from './PfBits';

export interface LogCrewOption {
  userId: string;
  name: string;
}

export interface LogUnitOption {
  /** `owned:<equipmentId>` or `rent_in:<rentInId>`. */
  key: string;
  kind: 'owned' | 'rent_in';
  equipmentId: string | null;
  rentInId: string | null;
  name: string;
  detail: string | null;
  /** Rent-ins only: the rent window, to offer the unit on its days only. */
  onRentOn?: string | null;
  offRentOn?: string | null;
}

export interface LogValues {
  weather: string | null;
  narrative: string | null;
  safetyNotes: string | null;
  progressFromChainage: number | null;
  progressToChainage: number | null;
  crew: { userId: string; name: string | null; hours: number; standbyHours: number; notes: string | null }[];
  equipment: {
    kind: string;
    equipmentId: string | null;
    rentInId: string | null;
    name: string | null;
    hours: number;
    standbyHours: number;
    hourMeterEnd: number | null;
    equipmentCheckId: string | null;
    notes: string | null;
  }[];
}

interface CrewRow {
  userId: string;
  name: string;
  hours: string;
  standby: string;
  notes: string;
}
interface UnitRow extends LogUnitOption {
  hours: string;
  standby: string;
  meter: string;
  checkId: string;
  notes: string;
}

export const unitKey = (kind: string, equipmentId: string | null, rentInId: string | null) =>
  kind === 'rent_in' ? `rent_in:${rentInId}` : `owned:${equipmentId}`;

const onRentOn = (u: LogUnitOption, day: string) =>
  u.kind !== 'rent_in' || ((!u.onRentOn || u.onRentOn <= day) && (!u.offRentOn || u.offRentOn >= day));

function hoursError(label: string, v: string, required: boolean): string | null {
  if (!v.trim()) return required ? `${label}: enter the hours.` : null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0 || n > 24) return `${label}: hours must be between 0 and 24.`;
  return null;
}

/** The write body from the form rows. Exported for the test. */
export function logWriteBody(
  header: { weather: string; narrative: string; safety: string; from: string; to: string },
  crew: CrewRow[],
  units: UnitRow[],
): PfDailyLogWrite {
  return {
    weather: blankToNull(header.weather),
    narrative: blankToNull(header.narrative),
    safety_notes: blankToNull(header.safety),
    progress_from_chainage: blankToNull(header.from),
    progress_to_chainage: blankToNull(header.to),
    crew: crew
      .filter((c) => c.hours.trim() !== '')
      .map((c) => ({ user_id: c.userId, hours: c.hours.trim(), standby_hours: blankToNull(c.standby), notes: blankToNull(c.notes) })),
    equipment: units
      .filter((u) => u.hours.trim() !== '')
      .map((u) => ({
        ...(u.kind === 'rent_in' ? { rent_in_id: u.rentInId as string } : { equipment_id: u.equipmentId as string }),
        hours: u.hours.trim(),
        standby_hours: blankToNull(u.standby),
        // Owned units only — a rent-in never sends a meter.
        ...(u.kind === 'owned' ? { hour_meter_end: blankToNull(u.meter) } : {}),
        equipment_check_id: blankToNull(u.checkId),
        notes: blankToNull(u.notes),
      })),
  };
}

export function LogEditor({
  workDate,
  values,
  crewOptions,
  unitOptions,
  checks,
  chainageUnit,
  pending,
  error,
  submitLabel,
  readOnly = false,
  fileCheckState,
  onCancel,
  onSubmit,
}: {
  workDate: string;
  values: LogValues | null;
  crewOptions: LogCrewOption[];
  unitOptions: LogUnitOption[];
  checks: PfPreUseCheck[];
  chainageUnit: string | null;
  pending: boolean;
  error: string | null;
  submitLabel: string;
  readOnly?: boolean;
  /** When given, each unit with registered equipment offers "File a pre-use check" with this navigation state. */
  fileCheckState?: (unit: LogUnitOption) => { to: string; state: unknown } | null;
  onCancel?: () => void;
  onSubmit: (body: PfDailyLogWrite) => void;
}) {
  const [header, setHeader] = useState({
    weather: values?.weather ?? '',
    narrative: values?.narrative ?? '',
    safety: values?.safetyNotes ?? '',
    from: toInput(values?.progressFromChainage),
    to: toInput(values?.progressToChainage),
  });

  const initialCrew = useMemo<CrewRow[]>(() => {
    const rows = new Map<string, CrewRow>();
    for (const c of crewOptions) rows.set(c.userId, { userId: c.userId, name: c.name, hours: '', standby: '', notes: '' });
    for (const c of values?.crew ?? [])
      rows.set(c.userId, {
        userId: c.userId,
        name: c.name ?? rows.get(c.userId)?.name ?? '—',
        hours: toInput(c.hours),
        standby: c.standbyHours ? toInput(c.standbyHours) : '',
        notes: c.notes ?? '',
      });
    return Array.from(rows.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const initialUnits = useMemo<UnitRow[]>(() => {
    const rows = new Map<string, UnitRow>();
    for (const u of unitOptions)
      if (onRentOn(u, workDate)) rows.set(u.key, { ...u, hours: '', standby: '', meter: '', checkId: '', notes: '' });
    for (const e of values?.equipment ?? []) {
      const key = unitKey(e.kind, e.equipmentId, e.rentInId);
      const opt = unitOptions.find((u) => u.key === key);
      rows.set(key, {
        key,
        kind: e.kind === 'rent_in' ? 'rent_in' : 'owned',
        equipmentId: opt?.equipmentId ?? e.equipmentId,
        rentInId: e.rentInId,
        name: e.name ?? opt?.name ?? '—',
        detail: opt?.detail ?? null,
        hours: toInput(e.hours),
        standby: e.standbyHours ? toInput(e.standbyHours) : '',
        meter: toInput(e.hourMeterEnd),
        checkId: e.equipmentCheckId ?? '',
        notes: e.notes ?? '',
      });
    }
    return Array.from(rows.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [crew, setCrew] = useState<CrewRow[]>(initialCrew);
  const [units, setUnits] = useState<UnitRow[]>(initialUnits);
  const [localError, setLocalError] = useState<string | null>(null);

  const setCrewRow = (i: number, patch: Partial<CrewRow>) => setCrew((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const setUnitRow = (i: number, patch: Partial<UnitRow>) => setUnits((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const unit = chainageUnit ? ` (${chainageUnit})` : '';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!!header.from.trim() !== !!header.to.trim()) return setLocalError('Enter both progress chainages, or neither.');
    if (header.from.trim() && header.to.trim() && Number(header.to) < Number(header.from))
      return setLocalError('The progress "to" chainage cannot be before "from".');
    for (const c of crew) {
      const err = hoursError(c.name, c.hours, false) ?? (c.hours.trim() ? hoursError(`${c.name} standby`, c.standby, false) : null);
      if (err) return setLocalError(err);
    }
    for (const u of units) {
      const err = hoursError(u.name, u.hours, false) ?? (u.hours.trim() ? hoursError(`${u.name} standby`, u.standby, false) : null);
      if (err) return setLocalError(err);
      if (!u.hours.trim() && (u.meter.trim() || u.checkId)) return setLocalError(`${u.name}: enter its hours, or clear the meter and check.`);
    }
    onSubmit(logWriteBody(header, crew, units));
  };

  const checksFor = (u: UnitRow) => (u.equipmentId ? checks.filter((c) => c.equipmentId === u.equipmentId) : []);

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="pf-log-form">
      <PfFormError message={localError ?? error} />
      <fieldset disabled={readOnly} className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <PfField label="Weather">
            <input className={PF_INPUT} aria-label="Weather" value={header.weather} onChange={(e) => setHeader({ ...header, weather: e.target.value })} />
          </PfField>
          <PfField label={`Progress from${unit}`}>
            <input className={PF_INPUT} inputMode="decimal" aria-label="Progress from" value={header.from} onChange={(e) => setHeader({ ...header, from: e.target.value })} />
          </PfField>
          <PfField label={`Progress to${unit}`}>
            <input className={PF_INPUT} inputMode="decimal" aria-label="Progress to" value={header.to} onChange={(e) => setHeader({ ...header, to: e.target.value })} />
          </PfField>
        </div>
        <PfField label="Work done">
          <textarea className={`${PF_INPUT} h-24 py-2`} aria-label="Work done" value={header.narrative} onChange={(e) => setHeader({ ...header, narrative: e.target.value })} />
        </PfField>
        <PfField label="Safety notes">
          <textarea className={`${PF_INPUT} h-20 py-2`} aria-label="Safety notes" value={header.safety} onChange={(e) => setHeader({ ...header, safety: e.target.value })} />
        </PfField>

        <section data-testid="pf-log-crew">
          <h3 className="mb-2 text-[0.68rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Crew hours — leave blank if they did not work</h3>
          {crew.length === 0 ? (
            <p className="text-sm text-fieldiq-muted">No one is on this job's crew. Add the crew on the job first.</p>
          ) : (
            <div className="space-y-2">
              {crew.map((c, i) => (
                <div key={c.userId} className="grid gap-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 p-3 sm:grid-cols-[minmax(0,1fr)_7rem_7rem_minmax(0,1fr)] sm:items-end">
                  <p className="text-sm font-extrabold text-white sm:pb-2">{c.name}</p>
                  <PfField label="Hours">
                    <input className={PF_INPUT} inputMode="decimal" aria-label={`${c.name} hours`} value={c.hours} onChange={(e) => setCrewRow(i, { hours: e.target.value })} />
                  </PfField>
                  <PfField label="Standby">
                    <input className={PF_INPUT} inputMode="decimal" aria-label={`${c.name} standby hours`} value={c.standby} onChange={(e) => setCrewRow(i, { standby: e.target.value })} />
                  </PfField>
                  <PfField label="Notes">
                    <input className={PF_INPUT} aria-label={`${c.name} notes`} value={c.notes} onChange={(e) => setCrewRow(i, { notes: e.target.value })} />
                  </PfField>
                </div>
              ))}
            </div>
          )}
        </section>

        <section data-testid="pf-log-units">
          <h3 className="mb-2 text-[0.68rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Unit hours — leave blank if it did not run</h3>
          {units.length === 0 ? (
            <p className="text-sm text-fieldiq-muted">No owned unit is on this job and no rent-in is on rent on {pfDay(workDate)}.</p>
          ) : (
            <div className="space-y-2">
              {units.map((u, i) => {
                const options = checksFor(u);
                const filing = fileCheckState && u.equipmentId ? fileCheckState(u) : null;
                return (
                  <div key={u.key} data-testid={`pf-log-unit-${u.kind}`} className="space-y-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 p-3">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-sm font-extrabold text-white">{u.name}</p>
                      <p className="text-xs text-fieldiq-muted">
                        {u.kind === 'rent_in' ? 'Rent-in' : 'Owned'}
                        {u.detail ? ` · ${u.detail}` : ''}
                      </p>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-4">
                      <PfField label="Hours">
                        <input className={PF_INPUT} inputMode="decimal" aria-label={`${u.name} hours`} value={u.hours} onChange={(e) => setUnitRow(i, { hours: e.target.value })} />
                      </PfField>
                      <PfField label="Standby">
                        <input className={PF_INPUT} inputMode="decimal" aria-label={`${u.name} standby hours`} value={u.standby} onChange={(e) => setUnitRow(i, { standby: e.target.value })} />
                      </PfField>
                      {u.kind === 'owned' && (
                        <PfField label="Hour meter at end">
                          <input className={PF_INPUT} inputMode="decimal" aria-label={`${u.name} hour meter end`} value={u.meter} onChange={(e) => setUnitRow(i, { meter: e.target.value })} />
                        </PfField>
                      )}
                      <PfField label="Pre-use check">
                        <select
                          className={PF_INPUT}
                          aria-label={`${u.name} pre-use check`}
                          value={u.checkId}
                          disabled={!u.equipmentId}
                          onChange={(e) => setUnitRow(i, { checkId: e.target.value })}
                        >
                          <option value="">{!u.equipmentId ? 'Not a registered unit' : options.length ? 'None linked' : 'None filed this day'}</option>
                          {options.map((c) => (
                            <option key={c.id} value={c.id}>
                              {(c.status ?? '—').replace(/_/g, ' ')} · {c.checkedBy ?? '—'}
                            </option>
                          ))}
                          {u.checkId && !options.some((c) => c.id === u.checkId) && <option value={u.checkId}>Linked check</option>}
                        </select>
                      </PfField>
                    </div>
                    <PfField label="Notes">
                      <input className={PF_INPUT} aria-label={`${u.name} unit notes`} value={u.notes} onChange={(e) => setUnitRow(i, { notes: e.target.value })} />
                    </PfField>
                    {filing && !readOnly && (
                      <Link to={filing.to} state={filing.state} className="inline-flex items-center gap-1.5 text-sm font-bold text-fi-amber hover:underline">
                        <ClipboardCheck className="size-4" aria-hidden="true" />
                        File a pre-use check
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </fieldset>

      {!readOnly && (
        <div className="flex flex-wrap justify-end gap-2">
          {onCancel && (
            <button type="button" className={PF_SECONDARY} onClick={onCancel} disabled={pending}>
              Cancel
            </button>
          )}
          <button type="submit" className={PF_PRIMARY} disabled={pending}>
            {pending ? 'Saving…' : submitLabel}
          </button>
        </div>
      )}
    </form>
  );
}
